/**
 * Kasus Indisipliner (tabel kasus_indisipliner).
 * Plan: plan/PLAN_KASUS_INDISIPLINER.md · Panduan: docs/PANDUAN_KASUS_INDISIPLINER.md
 *
 * Kasus dibuat di server (functions/src/rekapIndisipliner.ts). Aplikasi hanya
 * membaca kasus milik pengguna dan mencatat tindakannya:
 *   wali kelas → (delegasi guru halaqoh, khusus Tahfidz; atau delegasi murobbi
 *   asrama santri, Kelas maupun Tahfidz, tanpa syarat jumlah kasus) → (teruskan ke Waka
 *   Kesiswaan / Kepala Divisi setelah santri punya `batas_teruskan` kasus yang
 *   sudah ditindak di semester berjalan).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../utils/supabasePagination';
import { useAuth } from './useAuth';
import { useUserAccess } from '../context/UserAccessContext';
import { useGuru } from '../context/GuruContext';
import { useMasterData } from '../context/MasterDataContext';
import { useSantri } from '../context/SantriContext';

export type StatusKasus = 'wali_kelas' | 'halaqoh' | 'murobbi' | 'diteruskan' | 'selesai' | 'batal';
export const STATUS_TERBUKA: StatusKasus[] = ['wali_kelas', 'halaqoh', 'murobbi', 'diteruskan'];

export interface KasusIndisipliner {
    id: string;
    santri_id: string;
    santri_nama: string;
    kelas_nama: string | null;
    jenjang: string | null;
    gender: string | null;
    wali_kelas_nama: string | null;
    kategori: 'Kelas' | 'Tahfidz';
    jenis: 'pekanan' | 'beruntun';
    periode_mulai: string;
    periode_selesai: string;
    jumlah: number;
    ringkasan: string | null;
    rincian: { tanggal: string; keterangan: string; mapel?: string }[] | null;
    tahun_ajaran: string | null;
    semester: string | null;
    bulan: string | null;
    status: StatusKasus;
    pemegang_role: string | null;
    pemegang_email: string | null;
    pemegang_nama: string | null;
    diteruskan_oleh_nama: string | null;
    catatan_penerusan: string | null;
    bukti_foto?: string[] | null;
    created_at: string;
}

export interface FilterKasus {
    tahunAjaran: string;   // '' = semua
    semester: string;      // 'Ganjil' | 'Genap' | ''
    bulan: string;         // 'YYYY-MM' | '' (kasus terbuka: bulan itu dan sebelumnya)
}

export interface PengaturanKasus {
    batasTindakHari: number;
    batasTeruskan: number;
}

export const PERAN_WAKA = 'Waka Kesiswaan';
export const PERAN_KADIV = 'Kepala Divisi';
export const PERAN_HALAQOH = 'Guru Tahfidz';
export const PERAN_MUROBBI = 'Murobbi';

/** Tahun ajaran & semester untuk sebuah tanggal (Juli = awal tahun ajaran). */
export const akademikDari = (d: Date = new Date()) => {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    return m >= 7
        ? { tahunAjaran: `${y}/${y + 1}`, semester: 'Ganjil' }
        : { tahunAjaran: `${y - 1}/${y}`, semester: 'Genap' };
};

/** Rentang tanggal (YYYY-MM-DD, selesai eksklusif) dari filter, untuk tabel tanpa kolom semester. */
export const rentangDariFilter = (f: FilterKasus): { mulai: string; selesai: string } | null => {
    if (f.bulan) {
        const [y, m] = f.bulan.split('-').map(Number);
        const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
        return { mulai: `${f.bulan}-01`, selesai: `${next}-01` };
    }
    const awal = Number((f.tahunAjaran || '').split('/')[0]);
    if (!awal) return null;
    if (f.semester === 'Ganjil') return { mulai: `${awal}-07-01`, selesai: `${awal + 1}-01-01` };
    if (f.semester === 'Genap') return { mulai: `${awal + 1}-01-01`, selesai: `${awal + 1}-07-01` };
    return { mulai: `${awal}-07-01`, selesai: `${awal + 1}-07-01` };
};

const quote = (v: string) => `"${String(v).replace(/"/g, '\\"')}"`;
const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Nama sama tanpa memperhatikan huruf besar dan spasi berlebih. */
const samaNama = (a?: string, b?: string) => {
    const n = (x?: string) => String(x || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return !!n(a) && n(a) === n(b);
};

/** Peran pengguna terhadap kasus indisipliner. */
export const useKasusScope = () => {
    const { authUser } = useAuth();
    const { checkUserAccess, isSuperAdmin } = useUserAccess();
    const { guruRecords } = useGuru();
    // murobbiRecords dimuat oleh halaman Kasus Indisipliner (tidak perlu untuk popup).
    const { kelasRecords, halaqohRecords, murobbiRecords, fetchKelasData, fetchHalaqohData } = useMasterData();
    const { santri } = useSantri();

    useEffect(() => { fetchKelasData(); }, [fetchKelasData]);
    useEffect(() => { fetchHalaqohData(); }, [fetchHalaqohData]);

    return useMemo(() => {
        const email = (authUser?.email || '').toLowerCase();
        const myGuru: any = guruRecords?.find((g: any) => (g.email || '').toLowerCase() === email);
        const myName: string = myGuru?.nama || authUser?.displayName || '';
        const divisi = String(myGuru?.divisi || '').toLowerCase();
        const can = (menu: string) => !!authUser?.email && checkUserAccess(authUser.email, menu);

        const kelasWali = (kelasRecords || []).filter(k => k.waliKelas && k.waliKelas === myName).map(k => k.namaKelas);
        const isWaliKelas = kelasWali.length > 0;
        const isWaka = can('pg-waka-kesiswaan');
        const isKadiv = can('pg-kepala-divisi');
        const isKesantrian = can('pg-kesantrian');

        const jenjangKadiv: string[] = [];
        let genderKadiv = '';
        if (isKadiv) {
            if (divisi.includes('paud')) jenjangKadiv.push('PAUD');
            if (/\bsd\b|sdit/.test(divisi)) jenjangKadiv.push('SD');
            if (divisi.includes('smp')) jenjangKadiv.push('SMP');
            if (divisi.includes('sma')) jenjangKadiv.push('SMA');
            if (divisi.includes('kesantrian putra')) { jenjangKadiv.push('SMP', 'SMA'); genderKadiv = 'Putra'; }
            if (divisi.includes('kesantrian putri')) { jenjangKadiv.push('SMP', 'SMA'); genderKadiv = 'Putri'; }
        }

        // Peran lama (delegasi sebelum fitur kasus): tim kesantrian putra/putri.
        const peranLama: string[] = [];
        if (isKesantrian) {
            if (myGuru?.jenisKelamin === 'Laki-laki') peranLama.push('SYS_KESANTRIAN_PUTRA');
            if (myGuru?.jenisKelamin === 'Perempuan') peranLama.push('SYS_KESANTRIAN_PUTRI');
        }

        /** Guru halaqoh tahfidz santri itu. */
        const guruHalaqohUntuk = (santriId: string): { nama: string; email: string } | null => {
            const h: any = (halaqohRecords || []).find((x: any) => (x.santri || []).some((s: any) => String(s.id) === String(santriId)));
            if (!h?.pengampu) return null;
            const g: any = guruRecords?.find((x: any) => x.nama === h.pengampu || x.id === h.pengampuId);
            return { nama: h.pengampu, email: (g?.email || '').toLowerCase() };
        };

        /** Murobbi asrama santri itu (bisa lebih dari satu, mis. murobbi dan mudabbir). */
        const murobbiUntuk = (santriId: string): { nama: string; email: string; mudabbir: boolean }[] => {
            const s: any = (santri || []).find((x: any) => String(x.id) === String(santriId));
            if (!s?.asrama) return [];
            return (murobbiRecords || [])
                .filter((m: any) => m.status !== 'Non Aktif' && (m.asramaBinaan || []).includes(s.asrama))
                .map((m: any) => {
                    // Data murobbi tidak menyimpan email; email diambil dari data guru dengan nama yang sama.
                    const g: any = guruRecords?.find((x: any) => samaNama(x.nama, m.nama));
                    return { nama: m.nama, email: (g?.email || '').toLowerCase(), mudabbir: !!m.isMudabbir };
                })
                .sort((a, b) => Number(a.mudabbir) - Number(b.mudabbir));
        };

        const lihatSemua = isSuperAdmin || isWaka;

        /** Bagian .or() untuk kasus terbuka yang terlihat oleh pengguna. */
        const orParts: string[] = [];
        if (email) orParts.push(`pemegang_email.eq.${email}`);
        if (kelasWali.length > 0) orParts.push(`kelas_nama.in.(${kelasWali.map(quote).join(',')})`);
        if (jenjangKadiv.length > 0) {
            const j = `jenjang.in.(${Array.from(new Set(jenjangKadiv)).join(',')})`;
            orParts.push(genderKadiv ? `and(${j},gender.eq.${genderKadiv})` : j);
        }
        if (peranLama.length > 0) orParts.push(`pemegang_role.in.(${peranLama.join(',')})`);

        const adaAkses = lihatSemua || orParts.length > 1 || isWaliKelas || isKadiv;

        /** Apakah pengguna memegang kasus ini (boleh bertindak), bukan sekadar memantau. */
        const memegang = (k: KasusIndisipliner): boolean => {
            if (isSuperAdmin) return true;
            if (k.status === 'wali_kelas') {
                return kelasWali.includes(k.kelas_nama || '') || (!!email && k.pemegang_email === email);
            }
            if (!!email && (k.pemegang_email || '').toLowerCase() === email) return true;
            if (k.status === 'diteruskan') {
                if (k.pemegang_role === PERAN_WAKA) return isWaka;
                if (k.pemegang_role === PERAN_KADIV) return isKadiv && jenjangKadiv.includes(k.jenjang || '');
                if (k.pemegang_role && peranLama.includes(k.pemegang_role)) return true;
            }
            return false;
        };

        const peranSaya = (k: KasusIndisipliner): string => {
            if (k.status === 'wali_kelas' && kelasWali.includes(k.kelas_nama || '')) return 'Wali Kelas';
            if (k.status === 'halaqoh') return PERAN_HALAQOH;
            if (k.status === 'murobbi') return PERAN_MUROBBI;
            if (k.status === 'diteruskan' && k.pemegang_role === PERAN_WAKA && isWaka) return PERAN_WAKA;
            if (k.status === 'diteruskan' && k.pemegang_role === PERAN_KADIV && isKadiv) return PERAN_KADIV;
            if (isSuperAdmin) return 'Super Admin';
            return k.pemegang_role || 'Guru';
        };

        return {
            email, myName, kelasWali, isWaliKelas, isWaka, isKadiv, isKesantrian, isSuperAdmin,
            jenjangKadiv, lihatSemua, orParts, adaAkses, memegang, peranSaya, guruHalaqohUntuk, murobbiUntuk,
            bolehRekapManual: isSuperAdmin || isKadiv || isWaka,
        };
    }, [authUser, checkUserAccess, isSuperAdmin, guruRecords, kelasRecords, halaqohRecords, murobbiRecords, santri]);
};

export type KasusScope = ReturnType<typeof useKasusScope>;

/** Query kasus terbuka yang terlihat oleh pengguna. */
const queryTerbuka = (scope: KasusScope, filter: FilterKasus | null, from: number, to: number) => {
    let q = supabase.from('kasus_indisipliner').select('*').in('status', STATUS_TERBUKA);
    if (filter?.tahunAjaran) q = q.eq('tahun_ajaran', filter.tahunAjaran);
    if (filter?.semester) q = q.eq('semester', filter.semester);
    // Bulan: kasus bulan itu + kasus bulan sebelumnya yang masih terbuka, supaya
    // kasus yang belum ditindak tidak "hilang" saat bulan berganti.
    if (filter?.bulan) q = q.lte('bulan', filter.bulan);
    if (!scope.lihatSemua) q = q.or(scope.orParts.join(','));
    return q.order('created_at', { ascending: false }).order('id').range(from, to);
};

export const bacaPengaturanKasus = async (): Promise<PengaturanKasus> => {
    const { data } = await supabase.from('alert_settings').select('*').eq('id', 1).maybeSingle();
    return {
        batasTindakHari: Number((data as any)?.batas_tindak_hari) || 7,
        batasTeruskan: Number((data as any)?.batas_teruskan) || 3,
    };
};

/** Kasus terbuka milik/pantauan pengguna + jumlah kasus yang sudah ditindak per santri di semester itu. */
export const useKasusIndisipliner = (filter: FilterKasus | null) => {
    const scope = useKasusScope();
    const [kasus, setKasus] = useState<KasusIndisipliner[]>([]);
    const [ditindakPerSantri, setDitindakPerSantri] = useState<Record<string, number>>({});
    const [pengaturan, setPengaturan] = useState<PengaturanKasus>({ batasTindakHari: 7, batasTeruskan: 3 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const filterKey = filter ? `${filter.tahunAjaran}|${filter.semester}|${filter.bulan}` : 'none';
    const scopeKey = `${scope.email}|${scope.lihatSemua}|${scope.orParts.join(',')}`;

    const muat = useCallback(async () => {
        if (!scope.email || (!scope.lihatSemua && scope.orParts.length === 0)) {
            setKasus([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [rows, set] = await Promise.all([
                fetchAllRows<KasusIndisipliner>((from, to) => queryTerbuka(scope, filter, from, to), { label: 'kasus_indisipliner' }),
                bacaPengaturanKasus(),
            ]);
            setKasus(rows);
            setPengaturan(set);

            // Jumlah kasus yang sudah ditindak per santri (semester berjalan) untuk syarat Teruskan.
            const ids = Array.from(new Set(rows.map(r => r.santri_id)));
            const counts: Record<string, number> = {};
            const ak = akademikDari();
            for (let i = 0; i < ids.length; i += 200) {
                const { data } = await supabase.from('kasus_indisipliner')
                    .select('santri_id')
                    .eq('status', 'selesai')
                    .eq('tahun_ajaran', ak.tahunAjaran)
                    .eq('semester', ak.semester)
                    .in('santri_id', ids.slice(i, i + 200))
                    .limit(1000);
                (data || []).forEach((r: any) => { counts[r.santri_id] = (counts[r.santri_id] || 0) + 1; });
            }
            setDitindakPerSantri(counts);
        } catch (e: any) {
            console.error('Gagal memuat kasus indisipliner:', e);
            setError(e?.message || 'Gagal memuat kasus.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey, scopeKey]);

    useEffect(() => { muat(); }, [muat]);

    return { scope, kasus, ditindakPerSantri, pengaturan, loading, error, muat };
};

/** Jumlah kasus terbuka yang dipegang pengguna (untuk popup Portal Guru). */
export const useJumlahKasusSaya = () => {
    const scope = useKasusScope();
    const [jumlah, setJumlah] = useState(0);
    const [terlambat, setTerlambat] = useState(0);
    const [loading, setLoading] = useState(true);
    const scopeKey = `${scope.email}|${scope.lihatSemua}|${scope.orParts.join(',')}`;

    useEffect(() => {
        let batal = false;
        (async () => {
            if (!scope.email || (!scope.lihatSemua && scope.orParts.length === 0)) { setLoading(false); return; }
            try {
                const [rows, set] = await Promise.all([
                    fetchAllRows<KasusIndisipliner>((from, to) => queryTerbuka(scope, null, from, to), { label: 'kasus_popup', onError: 'warn' }),
                    bacaPengaturanKasus(),
                ]);
                if (batal) return;
                const milik = rows.filter(scope.memegang);
                const batas = Date.now() - set.batasTindakHari * 24 * 60 * 60 * 1000;
                setJumlah(milik.length);
                setTerlambat(milik.filter(k => new Date(k.created_at).getTime() < batas).length);
            } finally {
                if (!batal) setLoading(false);
            }
        })();
        return () => { batal = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeKey]);

    return { jumlah, terlambat, loading };
};

// ============================================================================
// Aksi
// ============================================================================

interface Pelaku { email: string; nama: string; peran: string }

const kategoriLama = (k: KasusIndisipliner) => (k.jenis === 'pekanan' ? `${k.kategori}_Pekanan` : k.kategori);

/** Catat ke riwayat (alert_penanganan). Baris delegasi lama untuk kasus ini ikut ditutup. */
const catatRiwayat = async (k: KasusIndisipliner, data: { tindakan: string; catatan: string; buktiFoto?: string[] }, oleh: Pelaku, status: 'SELESAI' | 'DIBATALKAN') => {
    const isi = {
        status,
        tindakan: data.tindakan,
        catatan: data.catatan,
        bukti_foto: data.buktiFoto || [],
        tanggal_penanganan: today(),
        ditangani_oleh_email: oleh.email,
        ditangani_oleh_nama: oleh.nama,
    };
    const { data: lama } = await supabase.from('alert_penanganan')
        .update(isi).eq('kasus_id', k.id).eq('status', 'DELEGATED').select('id');
    if (lama && lama.length > 0) return;
    const { error } = await supabase.from('alert_penanganan').insert({
        ...isi,
        kasus_id: k.id,
        santri_id: k.santri_id,
        santri_nama: k.santri_nama,
        kelas_nama: k.kelas_nama,
        kategori: kategoriLama(k),
        detail_masalah: k.ringkasan,
    });
    if (error) throw error;
};

const ubah = async (id: string, data: Record<string, any>, statusSebelum: StatusKasus) => {
    // Hanya ubah bila status belum berubah (dua guru membuka kasus yang sama).
    const { data: rows, error } = await supabase.from('kasus_indisipliner')
        .update(data).eq('id', id).eq('status', statusSebelum).select('id');
    if (error) throw error;
    if (!rows || rows.length === 0) throw new Error('Kasus ini sudah diproses oleh guru lain. Muat ulang halaman.');
};

export const MAKS_BUKTI_FOTO = 3;

/** Unggah satu foto bukti (sudah dikompres) ke Storage, kembalikan URL publiknya. */
export const unggahBuktiFoto = async (file: File, kasusId: string): Promise<string> => {
    const path = `kasus_indisipliner/${kasusId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
    const { error } = await supabase.storage.from('images').upload(path, file, { contentType: 'image/jpeg' });
    if (error) throw new Error(`Gagal mengunggah foto bukti: ${error.message}`);
    return supabase.storage.from('images').getPublicUrl(path).data.publicUrl;
};

export const tindakKasus = async (k: KasusIndisipliner, data: { tindakan: string; catatan: string; buktiFoto?: string[] }, oleh: Pelaku) => {
    await ubah(k.id, {
        status: 'selesai',
        tindakan: data.tindakan,
        catatan: data.catatan || null,
        bukti_foto: (data.buktiFoto || []).slice(0, MAKS_BUKTI_FOTO),
        ditindak_oleh_email: oleh.email,
        ditindak_oleh_nama: oleh.nama,
        ditindak_oleh_peran: oleh.peran,
        ditindak_at: new Date().toISOString(),
    }, k.status);
    await catatRiwayat(k, data, oleh, 'SELESAI');
};

export const batalkanKasus = async (k: KasusIndisipliner, alasan: string, oleh: Pelaku) => {
    await ubah(k.id, {
        status: 'batal',
        tindakan: 'Dibatalkan',
        catatan: alasan,
        ditindak_oleh_email: oleh.email,
        ditindak_oleh_nama: oleh.nama,
        ditindak_oleh_peran: oleh.peran,
        ditindak_at: new Date().toISOString(),
    }, k.status);
    await catatRiwayat(k, { tindakan: 'Dibatalkan', catatan: alasan }, oleh, 'DIBATALKAN');
};

export const delegasiHalaqoh = async (k: KasusIndisipliner, guru: { nama: string; email: string }, catatan: string, oleh: Pelaku) => {
    if (k.kategori !== 'Tahfidz') throw new Error('Delegasi ke guru halaqoh hanya untuk kasus alpha tahfidz.');
    await ubah(k.id, {
        status: 'halaqoh',
        pemegang_role: PERAN_HALAQOH,
        pemegang_email: guru.email || null,
        pemegang_nama: guru.nama,
        diteruskan_oleh_email: oleh.email,
        diteruskan_oleh_nama: oleh.nama,
        diteruskan_at: new Date().toISOString(),
        catatan_penerusan: catatan || null,
    }, k.status);
};

/** Wali kelas minta bantuan murobbi asrama santri. Boleh untuk alpha kelas dan tahfidz, tanpa syarat jumlah kasus. */
export const delegasiMurobbi = async (k: KasusIndisipliner, murobbi: { nama: string; email: string }, catatan: string, oleh: Pelaku) => {
    await ubah(k.id, {
        status: 'murobbi',
        pemegang_role: PERAN_MUROBBI,
        pemegang_email: murobbi.email || null,
        pemegang_nama: murobbi.nama,
        diteruskan_oleh_email: oleh.email,
        diteruskan_oleh_nama: oleh.nama,
        diteruskan_at: new Date().toISOString(),
        catatan_penerusan: catatan || null,
    }, k.status);
};

export const teruskanKasus = async (k: KasusIndisipliner, ke: typeof PERAN_WAKA | typeof PERAN_KADIV, catatan: string, oleh: Pelaku) => {
    await ubah(k.id, {
        status: 'diteruskan',
        pemegang_role: ke,
        pemegang_email: null,
        pemegang_nama: ke === PERAN_KADIV ? `Kepala Divisi ${k.jenjang || ''}`.trim() : PERAN_WAKA,
        diteruskan_oleh_email: oleh.email,
        diteruskan_oleh_nama: oleh.nama,
        diteruskan_at: new Date().toISOString(),
        catatan_penerusan: catatan || null,
    }, k.status);
};

/** Guru halaqoh atau murobbi mengembalikan kasus ke wali kelas. */
export const kembalikanKeWaliKelas = async (k: KasusIndisipliner, catatan: string, oleh: Pelaku) => {
    await ubah(k.id, {
        status: 'wali_kelas',
        pemegang_role: null,
        pemegang_email: null,
        pemegang_nama: null,
        diteruskan_oleh_email: oleh.email,
        diteruskan_oleh_nama: oleh.nama,
        diteruskan_at: new Date().toISOString(),
        catatan_penerusan: catatan ? `Dikembalikan: ${catatan}` : 'Dikembalikan ke wali kelas',
    }, k.status);
};

// ============================================================================
// Rekap per santri (dipakai saat menindak)
// ============================================================================

export interface RiwayatSantriItem {
    id: string;
    tanggal: string;
    kategori: string;
    jenis: string;
    ringkasan: string;
    status: string;          // label tampil
    selesai: boolean;
    tindakan: string | null;
    catatan: string | null;
    penindak: string | null;
    peran: string | null;
    buktiFoto: string[];
}

export interface RekapSantri {
    tahunAjaran: string;
    totalKasus: number;
    ditindak: number;
    belum: number;
    alphaKelas: number;      // jam pelajaran
    alphaTahfidz: number;    // waktu halaqoh
    items: RiwayatSantriItem[];
}

const LABEL_STATUS: Record<string, string> = {
    wali_kelas: 'Di wali kelas', halaqoh: 'Di guru halaqoh', murobbi: 'Di murobbi', diteruskan: 'Diteruskan',
    selesai: 'Selesai', batal: 'Dibatalkan',
};

/**
 * Rekap satu santri selama satu tahun ajaran: kasus di kasus_indisipliner + riwayat
 * lama di alert_penanganan yang belum punya kasus_id (sebelum fitur kasus).
 * Jumlah alpha dijumlah dari kasus yang tidak dibatalkan; pekan yang tercakup
 * kasus beruntun tidak dibuatkan kasus pekanan, jadi tidak terhitung dua kali.
 */
export const bacaRekapSantri = async (santriId: string, tahunAjaran: string): Promise<RekapSantri> => {
    const awal = Number(tahunAjaran.split('/')[0]);
    const mulai = `${awal}-07-01`;
    const selesai = `${awal + 1}-07-01`;

    const [kasusRes, lamaRes] = await Promise.all([
        supabase.from('kasus_indisipliner')
            .select('id,kategori,jenis,periode_mulai,ringkasan,jumlah,status,tindakan,catatan,ditindak_oleh_nama,ditindak_oleh_peran,pemegang_nama,bukti_foto')
            .eq('santri_id', santriId)
            .eq('tahun_ajaran', tahunAjaran)
            .order('periode_mulai', { ascending: false })
            .limit(500),
        supabase.from('alert_penanganan')
            .select('id,kategori,detail_masalah,tindakan,catatan,tanggal_penanganan,ditangani_oleh_nama,status')
            .eq('santri_id', santriId)
            .is('kasus_id', null)
            .neq('status', 'DELEGATED')
            .not('kategori', 'like', 'Sakit%')
            .gte('tanggal_penanganan', mulai)
            .lt('tanggal_penanganan', selesai)
            .order('tanggal_penanganan', { ascending: false })
            .limit(500),
    ]);
    if (kasusRes.error) throw kasusRes.error;

    const kasus = kasusRes.data || [];
    const lama = lamaRes.data || [];

    const items: RiwayatSantriItem[] = [
        ...kasus.map((k: any) => ({
            id: k.id,
            tanggal: String(k.periode_mulai || '').substring(0, 10),
            kategori: k.kategori,
            jenis: k.jenis,
            ringkasan: k.ringkasan || '',
            status: LABEL_STATUS[k.status] || k.status,
            selesai: k.status === 'selesai' || k.status === 'batal',
            tindakan: k.tindakan,
            catatan: k.catatan,
            penindak: k.ditindak_oleh_nama || (k.status !== 'selesai' && k.status !== 'batal' ? k.pemegang_nama : null),
            peran: k.ditindak_oleh_peran,
            buktiFoto: k.bukti_foto || [],
        })),
        ...lama.map((r: any) => ({
            id: `lama-${r.id}`,
            tanggal: String(r.tanggal_penanganan || '').substring(0, 10),
            kategori: String(r.kategori || '').replace('_Pekanan', ''),
            jenis: String(r.kategori || '').includes('_Pekanan') ? 'pekanan' : 'beruntun',
            ringkasan: r.detail_masalah || '',
            status: r.status === 'DIBATALKAN' ? 'Dibatalkan' : 'Selesai',
            selesai: true,
            tindakan: r.tindakan,
            catatan: r.catatan,
            penindak: r.ditangani_oleh_nama,
            peran: null,
            buktiFoto: [],
        })),
    ].sort((a, b) => b.tanggal.localeCompare(a.tanggal));

    const aktif = kasus.filter((k: any) => k.status !== 'batal');
    const sum = (kat: string) => aktif.filter((k: any) => k.kategori === kat).reduce((s: number, k: any) => s + (Number(k.jumlah) || 0), 0);

    return {
        tahunAjaran,
        totalKasus: items.filter(i => i.status !== 'Dibatalkan').length,
        ditindak: items.filter(i => i.status === 'Selesai').length,
        belum: items.filter(i => !i.selesai).length,
        alphaKelas: sum('Kelas'),
        alphaTahfidz: sum('Tahfidz'),
        items,
    };
};
